import React from "react";
import { TFunction } from "i18next";
import capitalize from "lodash/capitalize";
import styled from "styled-components";
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons/faUpRightFromSquare";

import { TagList } from "../../../components/TagList";
import { AssigneeBanner } from "../../../components/AssigneeBanner";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { TextWithTruncated } from "../../../components/TextWithTruncated";
import { EntriesTableSettings } from "../../../model/entries-table-settings/EntriesTableSettings";
import { EntriesTableMainFields } from "../../../model/entries-table-settings/types/EntriesTableMainFields.enum";
import { Entry } from "../../../model/entry/Entry";
import { Form } from "../../../model/form/Form";
import { InputTypes } from "../../../model/form/types/InputTypes.enum";
import { Permission } from "../../../model/permissions/Permission";
import { User } from "../../../model/user/User";
import { standardDate } from "../../../utils/Date";
import { isValidUrl } from "../../../utils/String";
import { Tooltip } from "../../../utils/local-antd";

const SourceButton = styled(Button)`
  padding: 0;
  margin: 0;
  background-color: transparent;
  color: ${({ theme }) => theme.main};
`;

const APPENDABLE_TYPES: string[] = [InputTypes.TEXT, InputTypes.TEXTAREA];

const getFormTitle = (t: TFunction, form?: Form) => {
  if (form?.title) {
    return (
      <TextWithTruncated length={25} fontSize="s">
        {form.orphan ? t("forms.manual_entry") : form.title}
      </TextWithTruncated>
    );
  }

  return <Text fontSize="s">{t("entries.table.entry.unknown_title")}</Text>;
};

export type DuplicateEntryFieldDescriptor = {
  key: string;
  label: string;
  actionable: boolean;
  isMainField: boolean;
  resolveType: (entry: Entry) => InputTypes | undefined;
  getValue: (entry: Entry) => string;
  formatValue: (entry: Entry) => string;
  canAppend: (entry: Entry) => boolean;
  renderReadOnly: (entry: Entry) => React.ReactNode;
};

// Mirrors the API's EntryContacts match keys, so a card says "same as kept"
// exactly when the API treats the two values as the same
const normalizeValue = (value: string, field: string) => {
  if (field === EntriesTableMainFields.PHONE) {
    return value.replace(/[\s\-()./]/g, "");
  }

  if (field === EntriesTableMainFields.EMAIL) {
    return value.trim().toLowerCase();
  }

  return value.trim();
};

export const isValueCovered = (
  rawKeptValue: string,
  rawOtherValue: string,
  { key, isMainField }: Pick<DuplicateEntryFieldDescriptor, "key" | "isMainField">,
) => {
  // Checkbox and number fields are stored as booleans and numbers
  const keptValue = String(rawKeptValue);
  const other = normalizeValue(String(rawOtherValue), key);

  if (!other) {
    return true;
  }

  if (isMainField) {
    return normalizeValue(keptValue, key) === other;
  }

  return (
    keptValue.trim() === other ||
    keptValue
      .split(",")
      .map((part) => part.trim())
      .includes(other)
  );
};

// The description is a column like any other, but edits keep it on the entry
// itself. One given when the entry was created is still in its fields, which
// the table falls back to the same way.
export const getEntryFieldValue = (entry: Entry, key: string): string =>
  (key === "description"
    ? entry.description || entry.fields?.description
    : entry.fields?.[key]) ?? "";

export const withEntryFieldValue = (
  entry: Entry,
  key: string,
  value: string,
): Partial<Entry> =>
  key === "description"
    ? { description: value }
    : { fields: { ...entry.fields, [key]: value } };

const createReadOnlyDescriptor = (
  key: string,
  label: string,
  renderReadOnly: DuplicateEntryFieldDescriptor["renderReadOnly"],
): DuplicateEntryFieldDescriptor => ({
  key,
  label,
  actionable: false,
  isMainField: false,
  resolveType: () => undefined,
  getValue: () => "",
  formatValue: () => "",
  canAppend: () => false,
  renderReadOnly,
});

const createActionableDescriptor = (
  key: string,
  label: string,
  isMainField: boolean,
  resolveType: DuplicateEntryFieldDescriptor["resolveType"],
  user: User,
  t: TFunction,
): DuplicateEntryFieldDescriptor => {
  const getValue = (entry: Entry) => getEntryFieldValue(entry, key);

  return {
    key,
    label,
    actionable: true,
    isMainField,
    resolveType,
    getValue,
    formatValue: (entry) => {
      const type = resolveType(entry)?.toLowerCase();

      if (type === InputTypes.DATE.toLowerCase()) {
        return standardDate(getValue(entry), user) as string;
      }

      if (type === InputTypes.CHECKBOX.toLowerCase()) {
        const value = String(getValue(entry));

        return value ? t(value === "true" ? "common.yes" : "common.no") : "";
      }

      return getValue(entry);
    },
    canAppend: (entry) => {
      const type = resolveType(entry)?.toLowerCase();

      return !isMainField && (!type || APPENDABLE_TYPES.includes(type));
    },
    renderReadOnly: () => null,
  };
};

export function getDuplicateEntryFieldDescriptors(
  columns: EntriesTableSettings["columns"],
  ctx: {
    t: TFunction;
    permissions: Permission["permissions"];
    user: User;
    members?: User[];
  },
): DuplicateEntryFieldDescriptor[] {
  const { t, permissions, user, members } = ctx;

  return columns
    .filter((c) => c.field !== "actions" && c.field !== "task")
    .map((column): DuplicateEntryFieldDescriptor => {
      const { field, label } = column;
      const isMainField = [
        EntriesTableMainFields.EMAIL,
        EntriesTableMainFields.PHONE,
      ].includes(field as EntriesTableMainFields);

      switch (true) {
        case field === "tags":
          return createReadOnlyDescriptor(field, t("common.tags"), (entry) =>
            entry.tagIds?.length ? (
              <TagList tagIds={entry.tagIds} wrap />
            ) : null,
          );

        case field === "assignee":
          return createReadOnlyDescriptor(
            field,
            t("common.assignee"),
            (entry) => (
              <AssigneeBanner
                assignees={members?.filter((m) =>
                  entry.assigneeIds?.includes(m._id),
                )}
              />
            ),
          );

        case field === "source":
          return createReadOnlyDescriptor(
            field,
            t("common.source"),
            (entry) => {
              if (!entry.origin?.length) {
                return null;
              }
              const isURL = isValidUrl(entry.origin);

              return (
                <Flex alignItems="center" gap="s">
                  <TextWithTruncated length={20} fontSize="s" direction="ltr">
                    {isURL
                      ? entry.origin
                      : capitalize(
                          entry.origin === "direct"
                            ? t("common.direct")
                            : entry.origin,
                        )}
                  </TextWithTruncated>

                  {isURL ? (
                    <Tooltip title={t("common.openInNewTab")}>
                      <SourceButton
                        onClick={() => window.open(entry.origin, "_blank")}
                        icon={faUpRightFromSquare}
                        iconWidth="xl"
                      />
                    </Tooltip>
                  ) : null}
                </Flex>
              );
            },
          );

        case field === "created":
          return createReadOnlyDescriptor(
            field,
            t("entries.table.header.submit_time"),
            (entry) => (
              <Text fontSize="s">
                {standardDate(entry.createdAt, user, true) as string}
              </Text>
            ),
          );

        case field === "form":
          return createReadOnlyDescriptor(
            field,
            t("entries.table.header.form_title"),
            (entry) =>
              permissions.FORM.READ ? getFormTitle(t, entry.parentForm) : null,
          );

        case field === "name":
          return createActionableDescriptor(
            field,
            t("common.name"),
            isMainField,
            () => InputTypes.TEXT,
            user,
            t,
          );

        case field === "phone":
          return createActionableDescriptor(
            field,
            t("common.phone"),
            isMainField,
            () => InputTypes.PHONE,
            user,
            t,
          );

        case field === "email":
          return createActionableDescriptor(
            field,
            t("common.email"),
            isMainField,
            () => InputTypes.EMAIL,
            user,
            t,
          );

        case field.startsWith("custom-field"):
          return createActionableDescriptor(
            field,
            label,
            isMainField,
            (entry) =>
              entry.parentForm?.inputs?.find((input) => input.name === field)
                ?.type,
            user,
            t,
          );

        default:
          return createActionableDescriptor(
            field,
            t(`common.${field}`),
            isMainField,
            () => InputTypes.TEXT,
            user,
            t,
          );
      }
    });
}
